import moment from 'moment';
import { dateFormatRegionMap } from '../constants/dateFormat';
import formatToUnits from '@app/ui/sports-components/utils/formatToUnits';
import getCurrencySymbol from './getCurrencySymbol';

const forceLocale = (num, places) =>
    new Number(num).toLocaleString({}, { minimumFractionDigits: places });

export const shortCurrency = (value, decimalPlaces = 0, currency = 'USD') => {
    const symbol = getCurrencySymbol(currency);
    const places = value < 1000 ? 0 : decimalPlaces;
    const [num, suffix] = formatToUnits(value, places);
    const localValue = forceLocale(num, places);
    return `${symbol}${localValue}${suffix}`;
};

export const shortDecimal = (value, decimalPlaces = 0) => {
    const places = value < 1000 ? 0 : decimalPlaces;
    const [num, suffix] = formatToUnits(value, places);
    const localValue = forceLocale(num, places);
    return `${localValue}${suffix}`;
};

const checkIfNumber = num => num && typeof num === 'number';

export const splitWordsByMaxLineLength = (str, maxLineLength) => {
    const words = str.trim().split(' ');
    const linesArray = [];
    let line = '';

    words.forEach(word => {
        if (line && (line + word).length > maxLineLength) {
            linesArray.push(line.trim());
            line = '';
        }
        line += `${word} `;
    });
    if (line) linesArray.push(line.trim());
    return linesArray;
};

export const trimDate = str => {
    if (typeof str !== 'string' || !str) {
        return '';
    }

    return str.replace(/\s?\(?\d{2}-\d{2}-\d{4}\)?\s?/, ' ').trim();
};

// Formats num {Number} to USD {String}
export const currency = (
    num,
    { currency = 'USD' } = {},
    shouldDisplayPositiveSign = false,
) => {
    if (Number.isNaN(parseFloat(num))) return null;
    const sign = num < 0 ? '-' : shouldDisplayPositiveSign ? '+' : '';
    const value = new Number(Math.abs(num)).toLocaleString(undefined, {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });
    return `${sign}${value}`;
};

// Formats num {Number} to Percentage {String}
export const percentage = (
    num,
    options = {},
    shouldDisplayPositiveSign = false,
) => {
    if (checkIfNumber(num)) {
        const positiveSign = num > 0 && shouldDisplayPositiveSign ? '+' : '';
        const formattedValue = num.toLocaleString(undefined, {
            style: 'percent',
            ...options,
        });
        return `${positiveSign}${formattedValue}`;
    }
    return '0%';
};

// Formats num {Number} to fixed raw string. ie. (en-US) 123,456.8976 => 123456.89
export const rawNumber = (num, fractionDigits = 2) => {
    if (checkIfNumber(num)) {
        return Number(num).toFixed(fractionDigits);
    }
    return 0;
};

export const decimalIgnoreNan = num => decimal(num, true, false, true);

export const asWeekDay = value =>
    new Date(value).toLocaleDateString('default', { weekday: 'long' });

// Formats num {Number} to Decimal {String}
export const decimal = (
    num,
    roundNumber = false,
    shouldDisplayPositiveSign = false,
    ignoreNan = false,
) => {
    if (checkIfNumber(num)) {
        const positiveSign = num > 0 && shouldDisplayPositiveSign ? '+' : '';
        if (!roundNumber) {
            const formattedValue = num.toLocaleString();
            return `${positiveSign}${formattedValue}`;
        } else {
            const formattedValue = Math.round(num).toLocaleString();
            return `${positiveSign}${formattedValue}`;
        }
    }
    return ignoreNan ? null : '0';
};

export const abbreviatedDecimal = (num, fixed = true, customNoDataLabel) => {
    if (checkIfNumber(num)) {
        fixed = !fixed || fixed < 0 ? 0 : fixed; // number of decimal places to show
        let b = num.toPrecision(2).split('e'); // get power
        let k =
            b.length === 1 ? 0 : Math.floor(Math.min(b[1].slice(1), 14) / 3); // floor at decimals, ceiling at trillions
        let c =
            k < 1
                ? num.toFixed(0 + fixed)
                : (num / Math.pow(10, k * 3)).toFixed(0 + fixed); // divide by power
        let d = c < 0 ? c : Math.abs(c); // enforce -0 is 0
        return d + ['', 'K', 'M', 'B', 'T'][k]; // append power
    }
    return customNoDataLabel || '0';
};

// Formats date to short ISO
export const shortISODate = str => new Date(str).toISOString().split('T')[0];

// Formats str {String} to month/day/year {String}
export const shortDate = str => {
    const dateRegion = localStorage.getItem('mvc-date-locale') || 'en-US';
    const format = dateFormatRegionMap[dateRegion].moment.short;

    return moment(str).format(format);
};

export const axisDate = str => {
    const dateRegion = localStorage.getItem('mvc-date-locale') || 'en-US';
    const format = dateFormatRegionMap[dateRegion].moment.axis;
    return moment(str).format(format);
};

// Formats str {String} to month/day/year {String}
export const longDate = (str, includeYear) => {
    const dateRegion = localStorage.getItem('mvc-date-locale') || 'en-US';
    const formatField = includeYear ? 'longWithYear' : 'long';
    const format = dateFormatRegionMap[dateRegion].moment[formatField];
    return moment(str).format(format);
};

export const formatHourAndMinutesFromTimestamp = timestamp => {
    const date = new Date(timestamp * 1000);
    const options = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
    };
    let formattedTime = date.toLocaleTimeString('en-US', options);

    return formattedTime.toLowerCase();
};

// Format bool {Bool} to icon component
// export const boolean = bool => <BooleanIndicator status={bool} />;

export const dateHour = str => {
    return new Date(str)
        .toLocaleDateString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
        })
        .replace(/[ ]*,[ ]*|[ ]+/g, ' ');
};

export const ordinalSuffix = num => {
    const j = num % 10,
        k = num % 100;

    if (j === 1 && k !== 11) {
        return 'st';
    }

    if (j === 2 && k !== 12) {
        return 'nd';
    }

    if (j === 3 && k !== 13) {
        return 'rd';
    }

    return 'th';
};
